import { useState, useEffect, useCallback } from 'react';
import { getClaimForEdit, updateClaimAsAdmin, processClaimAction, getClaimVersions } from '../service/adminService.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';



const useAdminClaimDetail = (claimType, claimId) => {
  // Data states
  const [claim, setClaim] = useState(null);
  const [originalClaim, setOriginalClaim] = useState(null);
  const [claimVersions, setClaimVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Comment states
  const [generalComments, setGeneralComments] = useState('');
  const [lineItemComments, setLineItemComments] = useState({});

  // Validation function
  const validateEditData = useCallback((data) => {
    const errors = [];
    
    if (claimType === 'perdiem') {
      if (!data.reasonForTrip?.trim()) {
        errors.push('Reason for trip is required');
      }
      if (!data.startDate) {
        errors.push('Start date is required');
      }
      if (!data.endDate) {
        errors.push('End date is required');
      }
      if (!data.dailyDetails || data.dailyDetails.length === 0) {
        errors.push('Daily details are required');
      }
      // Validate individual daily details
      data.dailyDetails?.forEach((day, index) => {
        if (!day.date) {
          errors.push(`Day ${index + 1}: Date is required`);
        }
        if (day.perDiemRate && day.perDiemRate < 0) {
          errors.push(`Day ${index + 1}: Per diem rate cannot be negative`);
        }
      });
    } else if (claimType === 'expense') {
      if (!data.items || data.items.length === 0) {
        errors.push('At least one expense item is required');
      }
      // Validate individual expense items
      data.items?.forEach((item, index) => {
        if (!item.expenditureDescription?.trim()) {
          errors.push(`Item ${index + 1}: Description is required`); }
        if (!item.receiptAmount || item.receiptAmount <= 0) {
          errors.push(`Item ${index + 1}: Valid amount is required`);
        }
        if (!item.expenditureDate) {
          errors.push(`Item ${index + 1}: Expenditure date is required`);
        }
      });
    }
    
    return errors;
  }, [claimType]);

  // Fetch claim data using admin service
  const fetchClaim = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the new admin service to get claim with edit history
      const response = await getClaimForEdit(claimType, claimId);
      const claimData = response.data || response;
      
      setClaim(claimData.current || claimData);
      setOriginalClaim(claimData.original || claimData.current || claimData);
      setEditData(claimData.current || claimData);
      
      // Pre-populate existing comments
      if (claimData.current?.adminComments || claimData.adminComments) {
        setGeneralComments(claimData.current?.adminComments || claimData.adminComments);
      }
      
      // Pre-populate line item comments
      const existingLineComments = {};
      const currentClaim = claimData.current || claimData;
      
      if (claimType === 'perdiem' && currentClaim.dailyDetails) {
        currentClaim.dailyDetails.forEach((day, index) => {
          if (day.adminComments) {
            existingLineComments[index] = day.adminComments;
          }
        });
      } else if (claimType === 'expense' && currentClaim.items) {
        currentClaim.items.forEach((item, index) => {
          if (item.adminComments) {
            existingLineComments[index] = item.adminComments;
          }
        });
      }
      setLineItemComments(existingLineComments);
      
    } catch (err) {
      secureLogger.error('Error fetching claim', { status: err.response?.status });
      setError(err.message || 'Failed to fetch claim');
    } finally {
      setLoading(false);
    }
  }, [claimType, claimId]);

  // Fetch claim versions for comparison
  const fetchClaimVersions = useCallback(async () => {
    try {
      const response = await getClaimVersions(claimType, claimId);
      setClaimVersions(response.data || response);
      return response.data || response;
    } catch (err) {
      secureLogger.error('Error fetching claim versions', { status: err.response?.status });
      throw err;
    }
  }, [claimType, claimId]);

  // Process claim action (approve, reject, return)
  const handleClaimAction = useCallback(async (action) => {
    if (!generalComments?.trim() && action !== 'approved') {
      throw new Error('Please add comments for this action.');
    }

    setProcessing(true);
    try {
      const result = await processClaimAction(claimType, claimId, {
        action,
        comments: generalComments,
        lineItemComments: action === 'returned' ? lineItemComments : undefined
      });
      
      // Update local state
      setClaim(prev => ({
        ...prev,
        status: action,
        adminComments: generalComments,
        processedAt: new Date().toISOString()
      }));
      
      return result;
    } catch (err) {
      secureLogger.error('Error processing claim', { status: err.response?.status });
      throw err;
    } finally {
      setProcessing(false);
    }
  }, [claimType, claimId, generalComments, lineItemComments]);

  // Save claim edits
  const handleSaveEdits = useCallback(async (isDraft = false) => {
    // Validate data before saving
    const errors = validateEditData(editData);
    if (!isDraft && errors.length > 0) {
      setValidationErrors(errors);
      throw new Error('Please fix validation errors before saving');
    }
    
    setValidationErrors([]);
    setProcessing(true);
    
    try {
      // Add admin comments to the edit data
      const dataToSave = {
        ...editData,
        adminComments: generalComments,
        status: isDraft ? 'draft' : (editData.status || 'submitted')
      };

      // Add line item comments
      if (claimType === 'perdiem' && dataToSave.dailyDetails) {
        dataToSave.dailyDetails = dataToSave.dailyDetails.map((day, index) => ({
          ...day,
          adminComments: lineItemComments[index] || day.adminComments
        }));
      } else if (claimType === 'expense' && dataToSave.items) {
        dataToSave.items = dataToSave.items.map((item, index) => ({
          ...item,
          adminComments: lineItemComments[index] || item.adminComments
        }));
      }

      const result = await updateClaimAsAdmin(claimType, claimId, dataToSave);
      const updatedClaim = result.data || result;
      
      // Update local state
      setClaim(updatedClaim);
      setOriginalClaim(JSON.parse(JSON.stringify(updatedClaim)));
      setEditData(updatedClaim);
      setIsEditing(false);
      setHasUnsavedChanges(false);
      
      return result;
    } catch (err) {
      secureLogger.error('Error saving claim edits', { status: err.response?.status });
      throw err;
    } finally {
      setProcessing(false);
    }
  }, [claimType, claimId, editData, generalComments, lineItemComments, validateEditData]);

  // Update edit data
  const updateEditData = useCallback((field, value) => {
    setEditData(prev => {
      const newData = { ...prev };
      
      // Handle nested field updates (e.g., "dailyDetails.0.breakfast")
      if (field.includes('.')) {
        const fieldParts = field.split('.');
        let current = newData;
        
        for (let i = 0; i < fieldParts.length - 1; i++) {
          const part = fieldParts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }
        
        current[fieldParts[fieldParts.length - 1]] = value;
      } else {
        newData[field] = value;
      }
      
      // Check if there are unsaved changes
      const hasChanges = JSON.stringify(newData) !== JSON.stringify(originalClaim);
      setHasUnsavedChanges(hasChanges);
      
      // Clear validation errors when user makes changes
      if (validationErrors.length > 0) {
        setValidationErrors([]);
      }
      
      return newData;
    });
  }, [originalClaim, validationErrors.length]);

  // Update line item in edit data
  const updateLineItem = useCallback((index, field, value) => {
    const arrayField = claimType === 'perdiem' ? 'dailyDetails' : 'items';
    
    setEditData(prev => {
      const newData = { ...prev };
      if (!newData[arrayField]) {
        newData[arrayField] = [];
      }
      
      newData[arrayField] = [...newData[arrayField]];
      newData[arrayField][index] = {
        ...newData[arrayField][index],
        [field]: value
      };
      
      // Recalculate totals if needed
      if (claimType === 'perdiem') {
        // Recalculate perdiem totals
        const dailyDetail = newData[arrayField][index];
        dailyDetail.totalMIE = (dailyDetail.breakfast || 0) + 
                              (dailyDetail.lunch || 0) + 
                              (dailyDetail.dinner || 0) + 
                              (dailyDetail.incidentals || 0);
        
        // Recalculate overall totals
        newData.totalMIE = newData[arrayField].reduce((sum, day) => sum + (day.totalMIE || 0), 0);
        newData.totalLodging = newData[arrayField].reduce((sum, day) => sum + (day.lodgingCost || 0), 0);
        newData.totalClaim = newData.totalMIE + newData.totalLodging;
      } else if (claimType === 'expense') {
        // Recalculate expense totals
        const item = newData[arrayField][index];
        if (item.receiptAmount && item.exchangeRate) {
          item.usdEquivalent = item.receiptAmount * item.exchangeRate;
        }
        
        // Recalculate overall total
        newData.totalUsdAmount = newData[arrayField].reduce((sum, item) => 
          sum + (item.usdEquivalent || 0), 0);
        newData.totalAmount = newData.totalUsdAmount; // For consistency
      }
      
      const hasChanges = JSON.stringify(newData) !== JSON.stringify(originalClaim);
      setHasUnsavedChanges(hasChanges);
      
      return newData;
    });
  }, [claimType, originalClaim]);

  // Update line item comment
  const updateLineItemComment = useCallback((index, comment) => {
    setLineItemComments(prev => ({
      ...prev,
      [index]: comment
    }));
  }, []);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setEditData(originalClaim);
    setIsEditing(false);
    setHasUnsavedChanges(false);
    setValidationErrors([]);
  }, [originalClaim]);

  // Check if claim can be processed
  const canProcessClaim = useCallback(() => {
    return claim && claim.status === 'submitted' && !processing;
  }, [claim, processing]);

  // Check if claim can be edited
  const canEditClaim = useCallback(() => {
    return claim && ['submitted', 'returned'].includes(claim.status) && !processing;
  }, [claim, processing]);

  // Initial fetch
  useEffect(() => {
    if (claimId && claimType) {
      fetchClaim();
    }
  }, [fetchClaim, claimId, claimType]);

  return {
    // Data
    claim,
    originalClaim,
    editData,
    claimVersions,
    
    // State
    loading,
    processing,
    error,
    isEditing,
    hasUnsavedChanges,
    validationErrors,
    
    // Comments
    generalComments,
    setGeneralComments,
    lineItemComments,
    updateLineItemComment,
    
    // Actions
    handleClaimAction,
    handleSaveEdits,
    updateEditData,
    updateLineItem,
    setIsEditing,
    cancelEdit,
    refreshClaim: fetchClaim,
    fetchClaimVersions,
    
    // Permissions
    canProcessClaim: canProcessClaim(),
    canEditClaim: canEditClaim()
  };
};

export default useAdminClaimDetail;

